import React from "react";
import { Link } from "react-router-dom";
import { Truck, Building2, Map, Snowflake, Layers, ShieldCheck, ChevronRight, Store, Phone } from "lucide-react";
import { Button } from "../components/ui/button";
import { DELIVERY_ZONES, DELIVERY_EXTRAS, WARRANTY_EXTENSIONS, STORE_INFO } from "../mock";

const ZONE_ICONS = { Building2, Map };

const DeliveryWarranty = () => {
  return (
    <div>
      {/* Breadcrumb */}
      <div className="bg-white border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center text-sm text-neutral-600 gap-1">
          <Link to="/" className="hover:text-[#0b2960]">Accueil</Link>
          <ChevronRight className="h-4 w-4" />
          <span className="text-[#0b2960] font-semibold">Livraison & Garantie</span>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-[#0b2960] text-white py-14">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="text-xs uppercase tracking-widest font-bold text-[#ff5a5f]">Grille tarifaire officielle</div>
          <h1 className="text-3xl md:text-5xl font-black mt-1">Livraison & Garantie</h1>
          <p className="mt-3 text-neutral-200 max-w-2xl mx-auto">
            Retrouvez ci-dessous nos tarifs de livraison sur Marseille et le département 13, ainsi que nos extensions de garantie optionnelles.
          </p>
        </div>
      </section>

      {/* Delivery zones */}
      <section className="max-w-7xl mx-auto px-4 py-14">
        <div className="text-center mb-8">
          <div className="text-xs uppercase tracking-widest font-bold text-[#e11d24]">Livraison</div>
          <h2 className="text-2xl md:text-3xl font-extrabold text-[#0b2960]">Nos zones de livraison</h2>
          <p className="text-sm text-neutral-600 mt-2 max-w-xl mx-auto">
            Livraison à domicile sous 48h ouvrées après validation de votre commande. Retrait en magasin gratuit.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {DELIVERY_ZONES.map((z) => {
            const Icon = ZONE_ICONS[z.icon] || Truck;
            return (
              <div key={z.id} className="relative bg-white border border-neutral-200 rounded-xl p-6 hover:shadow-lg hover:border-[#0b2960]/30 transition-all overflow-hidden">
                <div className="absolute -top-4 -right-4 h-24 w-24 rounded-full bg-[#0b2960]/5" />
                <div className="flex items-start gap-4 relative">
                  <div className="h-12 w-12 rounded-lg bg-[#0b2960] text-white flex items-center justify-center shrink-0">
                    <Icon className="h-6 w-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-[#0b2960] text-lg">{z.label}</h3>
                    <p className="text-sm text-neutral-600 mt-1">{z.sub}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-black text-[#e11d24] leading-none">{z.price}<span className="text-lg">€</span></div>
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">Livraison</div>
                  </div>
                </div>
              </div>
            );
          })}

          <div className="bg-gradient-to-br from-[#0b2960]/5 to-[#0b2960]/10 border-2 border-dashed border-[#0b2960]/30 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-lg bg-white text-[#0b2960] border-2 border-[#0b2960]/30 flex items-center justify-center shrink-0">
                <Snowflake className="h-6 w-6" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-[#0b2960]">Supplément frigo américain</h3>
                <p className="text-sm text-neutral-600 mt-1">Livraison de réfrigérateurs grandes dimensions (frigo américain, side-by-side, multi-portes larges).</p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-black text-[#0b2960] leading-none">+{DELIVERY_EXTRAS.americanFridgeSurcharge}<span className="text-base">€</span></div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-[#0b2960]/5 to-[#0b2960]/10 border-2 border-dashed border-[#0b2960]/30 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-lg bg-white text-[#0b2960] border-2 border-[#0b2960]/30 flex items-center justify-center shrink-0">
                <Layers className="h-6 w-6" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-[#0b2960]">Étages sans ascenseur</h3>
                <p className="text-sm text-neutral-600 mt-1">Prix par étage supplémentaire au-delà du rez-de-chaussée, lorsque l'immeuble ne dispose pas d'ascenseur.</p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-black text-[#0b2960] leading-none">+{DELIVERY_EXTRAS.perFloorNoElevator}<span className="text-base">€</span></div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">Par étage</div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 bg-yellow-50 border-l-4 border-[#e11d24] rounded-md p-4 text-sm text-neutral-800">
          <b>💡 Bon à savoir :</b> vous pouvez également passer récupérer votre commande <b>gratuitement</b> directement en magasin, au 304 Av. de la Capelette 13010 Marseille.
        </div>
      </section>

      {/* Warranty extensions */}
      <section className="bg-white border-y border-neutral-200 py-14">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8">
            <div className="text-xs uppercase tracking-widest font-bold text-[#e11d24]">Garantie</div>
            <h2 className="text-2xl md:text-3xl font-extrabold text-[#0b2960]">Extensions de garantie optionnelles</h2>
            <p className="text-sm text-neutral-600 mt-2 max-w-xl mx-auto">
              En plus de la garantie constructeur incluse avec votre appareil, prolongez votre tranquillité avec l'une de nos extensions.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {WARRANTY_EXTENSIONS.filter((w) => w.months > 0).map((w, idx) => {
              const highlighted = w.id === "ext6";
              return (
                <div key={w.id} className={`relative rounded-2xl border-2 p-6 transition-all ${highlighted ? "border-[#e11d24] bg-white shadow-xl scale-[1.02]" : "border-neutral-200 bg-white hover:border-[#0b2960]/30 hover:shadow-lg"}`}>
                  {highlighted && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#e11d24] text-white text-[11px] font-bold uppercase tracking-widest px-3 py-1 rounded-full">Le plus populaire</div>
                  )}
                  <div className="h-12 w-12 rounded-full bg-[#0b2960] text-white flex items-center justify-center mb-4">
                    <ShieldCheck className="h-6 w-6" />
                  </div>
                  <div className="text-xs uppercase tracking-widest font-bold text-neutral-500">Extension</div>
                  <div className="text-4xl font-black text-[#0b2960] leading-none mt-1">{w.months} mois</div>
                  <div className="mt-4 pt-4 border-t border-neutral-200">
                    <div className="text-3xl font-black text-[#e11d24]">+{w.price} €</div>
                    <div className="text-xs text-neutral-500 mt-1">supplément unique · sans engagement</div>
                  </div>
                  <ul className="mt-5 space-y-2 text-sm text-neutral-700">
                    <li className="flex items-start gap-2"><span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#e11d24]" /> Pièces et main d'œuvre couvertes</li>
                    <li className="flex items-start gap-2"><span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#e11d24]" /> Prise en charge dépannage</li>
                    <li className="flex items-start gap-2"><span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#e11d24]" /> Cumul avec la garantie constructeur</li>
                  </ul>
                </div>
              );
            })}
          </div>

          <div className="mt-8 text-center">
            <Link to="/boutique">
              <Button className="bg-[#e11d24] hover:bg-[#b8171d] text-white h-11 px-6 font-bold">Choisir un produit</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA contact */}
      <section className="max-w-7xl mx-auto px-4 py-14">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-[#0b2960] text-white rounded-2xl p-8">
            <Store className="h-8 w-8 text-[#ff5a5f]" />
            <h3 className="mt-3 text-xl font-black">Retrait gratuit en magasin</h3>
            <p className="mt-2 text-neutral-200 text-sm">Économisez les frais de livraison et venez récupérer votre commande directement chez nous.</p>
            <p className="mt-3 text-sm">📍 {STORE_INFO.address}</p>
            <Link to="/magasin">
              <Button className="mt-5 bg-white text-[#0b2960] hover:bg-neutral-100 font-bold">Voir le magasin</Button>
            </Link>
          </div>
          <div className="bg-[#e11d24] text-white rounded-2xl p-8">
            <Phone className="h-8 w-8" />
            <h3 className="mt-3 text-xl font-black">Une question sur la livraison ?</h3>
            <p className="mt-2 text-white/90 text-sm">Notre équipe est disponible pour toute demande particulière ou devis livraison hors zone.</p>
            <p className="mt-3 text-sm">📞 {STORE_INFO.phone}</p>
            <a href={`tel:${STORE_INFO.phone.replace(/[.\s]/g, "")}`}>
              <Button className="mt-5 bg-white text-[#e11d24] hover:bg-neutral-100 font-bold">Nous appeler</Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DeliveryWarranty;
